import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
	type ActionFunctionArgs,
	data,
	Form,
	useActionData,
	useNavigation,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import {
	CheckboxField,
	PublicStyledComboboxField,
	PublicStyledField,
	PublicStyledSelect,
	PublicStyledTextareaField,
} from '#app/components/forms.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import MarketingSpacer from '#app/components/marketing/spacer.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import StoreMapLocationChooser
	from '#app/components/showrooms/store-map-location-chooser.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { Country } from '#app/enums/country.ts'
import { Profession } from '#app/enums/profession.ts'
import { Provinces } from '#app/enums/provinces.ts'
import { ReasonForContact } from '#app/enums/reason-for-contact.ts'
import { RequestStatus } from '#app/enums/request-status.ts'
import { ContactSchema } from '#app/features/contact/schemas.ts'
import { sendLeadToAzure } from '#app/features/contact/send-to-azure.server.ts'
import { NewsletterForm } from '#app/features/newsletter/form.tsx'
import { newsletterFromContact } from '#app/features/newsletter/subscribe.server.ts'
import { useHashScroll } from '#app/hooks/use-hash-scroll.ts'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	LocalizedLink,
	type ISupportedLanguage,
	useLocale,
} from '#app/utils/i18n.tsx'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.ts'
import { logger } from '#app/utils/logger.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.tsx'
import { createToastHeaders } from '#app/utils/toast.server.ts'
import routes from '#routes/index.tsx'
import {
	type Route
} from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/contact.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const stores = await prisma.store.findMany({
		where: {
			published: true,
		},
		include: {
			storeImage: {
				take: 1,
			},
		},
	})

	return {
		stores: stores.map((store) => ({
			...store,
			storeImage: undefined,
			storeImageId: store.storeImage[0]!.id,
		})),

		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'contact',
			keys: {
				title: 'title',
				description: 'description',
				canonical: `${BASE_URL}${routes.public.downloads.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const locale = (await i18next.getLocale(request)) as ISupportedLanguage
	const t = await i18next.getFixedT(locale, 'contact')

	checkHoneypot(formData)

	const submission = parseWithZod(formData, {
		schema: ContactSchema,
	})

	if (submission.status !== 'success') {
		void logger.warn('Contact form validation failed', {
			metadata: {
				errors: submission.error,
			},
		})
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	// Time-based validation to prevent bot spam
	// Real users typically take at least 3 seconds to fill out the form
	const MIN_FORM_FILL_TIME_MS = 3000
	const timestamp = Number(submission.value.timestamp)
	const timeElapsed = Date.now() - timestamp

	if (timeElapsed < MIN_FORM_FILL_TIME_MS) {
		void logger.warn('Contact form submitted too quickly (likely bot)', {
			metadata: {
				email: submission.value.email,
				timeElapsed,
				minimumRequired: MIN_FORM_FILL_TIME_MS,
			},
		})
		// Return success to avoid revealing the anti-spam mechanism
		// But don't actually process the form
		return data(
			{ result: submission.reply(), status: 'success' },
			{
				status: 201,
				headers: await createToastHeaders({
					title: t('form.sent.title'),
					description: t('form.sent.description'),
					type: 'success',
				}),
			},
		)
	}

	// Email-based rate limiting to prevent spam
	// Check if the same email has submitted within the last 10 minutes
	const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000 // 10 minutes
	const rateLimitCutoff = new Date(Date.now() - RATE_LIMIT_WINDOW_MS)

	const recentSubmission = await prisma.contactRequest.findFirst({
		where: {
			email: submission.value.email,
			createdAt: {
				gte: rateLimitCutoff,
			},
		},
		select: {
			id: true,
			createdAt: true,
		},
		orderBy: {
			createdAt: 'desc',
		},
	})

	if (recentSubmission) {
		const timeSinceLastSubmission = Date.now() - recentSubmission.createdAt.getTime()
		const minutesRemaining = Math.ceil((RATE_LIMIT_WINDOW_MS - timeSinceLastSubmission) / 60000)

		void logger.warn('Contact form rate limit exceeded', {
			metadata: {
				email: submission.value.email,
				lastSubmissionId: recentSubmission.id,
				timeSinceLastSubmission,
				minutesRemaining,
			},
		})

		// Return success to avoid revealing the anti-spam mechanism
		// But don't actually process the form
		return data(
			{ result: submission.reply(), status: 'success' },
			{
				status: 201,
				headers: await createToastHeaders({
					title: t('form.sent.title'),
					description: t('form.sent.description'),
					type: 'success',
				}),
			},
		)
	}

	// Log contact form submission
	void logger.info('Contact form submitted', {
		metadata: {
			email: submission.value.email,
			profession: submission.value.profession,
			country: submission.value.country,
			newsletter: submission.value.newsletter,
		},
	})

	const reasonForContact = ReasonForContact.getByCode(
		submission.value.reasonForContact,
	)
	const leadType = reasonForContact ? String(reasonForContact?.key) : '0'

	try {
		await prisma.contactRequest.create({
			data: {
				name: submission.value.name,
				surname: submission.value.surname,
				address1: submission.value.address1,
				address2: submission.value.address2,
				city: submission.value.city,
				zip: submission.value.zip,
				province: submission.value.province,
				country: submission.value.country,
				phone: submission.value.phone,
				email: submission.value.email,
				profession: Number(submission.value.profession),
				company: submission.value.company,
				leadType,
				message: submission.value.message,
				privacy: submission.value.acceptsTerms,
				newsletter: submission.value.newsletter,
				status: RequestStatus.NEW.code,
			},
		})

		void logger.info('Contact request saved to database', {
			metadata: {
				email: submission.value.email,
			},
		})

		// Send to Azure (production only)
		if (process.env.NODE_ENV === 'production') {
			await sendLeadToAzure(
				{
					...submission.value,
					leadType,
					newsletterConsent: submission.value.newsletter ?? false,
				},
				{
					leadSource: 'Web - Company WebSite',
					subject: 'Contact by Sapienstone Web Site',
				},
			)

			if (submission.value.newsletter) {
				await newsletterFromContact(request, submission.value)
			}

			void logger.info('Contact request synced to Azure', {
				metadata: {
					email: submission.value.email,
				},
			})
		} else {
			void logger.info('Skipping Azure sync (not in production)', {
				metadata: {
					email: submission.value.email,
				},
			})
		}

		return data(
			{ result: submission.reply(), status: 'success' },
			{
				status: 201,
				headers: await createToastHeaders({
					title: t('form.sent.title'),
					description: t('form.sent.description'),
					type: 'success',
				}),
			},
		)
	} catch (error) {
		await logger.error('Failed to process contact request', {
			error,
			severity: 'high',
			metadata: {
				email: submission.value.email,
			},
		})
		throw error
	}
}

export default function ContactPage({ loaderData }: Route.ComponentProps) {
	const data = useActionData<typeof action>()
	const locale = useLocale()
	const { t } = useTranslation(['contact', 'common'])
	useHashScroll()
	const formRef = useRef<HTMLFormElement | null>(null)
	const navigation = useNavigation()

	const {
		navigation: { route },
	} = usePublicContext()

	const isSubmitting = navigation.state === 'submitting'

	useEffect(() => {
		route.setContent([
			{
				label: `${t('title')}.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const [form, fields] = useForm({
		id: 'contact-form',
		lastResult: data?.result,
		constraint: getZodConstraint(ContactSchema),
		shouldValidate: 'onSubmit',
		defaultValue: {
			timestamp: Date.now().toString(),
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: ContactSchema,
			})
		},
	})

	useEffect(() => {
		if (data?.result?.status === 'success') {
			form.reset()
		}
		// eslint-disable-next-line
	}, [data])

	if (!loaderData) {
		return null
	}

	return (
		<>
			<Form
				ref={formRef}
				method="POST"
				{...getFormProps(form)}
				className={cn(
					'container relative z-20 -translate-y-[clamp(30px,calc(2.5vw+3rem),5rem)]',
					'w-full',
					'grid grid-cols-1 gap-x-sm gap-y-xxs md:grid-cols-2',
				)}
			>
				<HoneypotInputs />
				<input {...getInputProps(fields.timestamp, { type: 'hidden' })} />
				<Text
					as={'div'}
					size={'h3'}
					type={'serif'}
					weight={'normal'}
					className={
						'col-span-full leading-[clamp(30px,calc(2.5vw+2rem),4.5rem)]'
					}
				>
					<Trans
						t={t}
						i18nKey={'form'}
						components={[
							<PublicStyledField
								key={0}
								className={'inline-block'}
								inputProps={{
									...getInputProps(fields.name, {
										type: 'text',
									}),
									className: 'placeholder:lowercase w-fit',
									autoComplete: 'first-name',
									onClick: () => {
										formRef.current?.scrollIntoView({
											behavior: 'smooth',
										})
									},
								}}
								errors={fields.name.errors}
								placeholder={t('common:form.fields.name')}
							/>,
							<PublicStyledField
								key={1}
								className={'inline-block'}
								placeholder={t('common:form.fields.surname')}
								inputProps={{
									...getInputProps(fields.surname, {
										type: 'text',
									}),
									className: 'placeholder:lowercase w-fit',
									autoComplete: 'family-name',
									onClick: () => {
										formRef.current?.scrollIntoView({
											behavior: 'smooth',
										})
									},
								}}
								errors={fields.surname.errors}
							/>,
							<PublicStyledSelect
								key={2}
								className={'inline-block'}
								placeholder={`${t('common:form.fields.profession.industry')}`.toLowerCase()}
								items={Profession.getSelectOptions(locale)}
								meta={fields.profession}
							/>,
							<PublicStyledField
								key={3}
								className={'inline-block'}
								placeholder={t('common:form.fields.profession.company')}
								inputProps={{
									...getInputProps(fields.company, {
										type: 'text',
									}),
									autoComplete: 'organization',
									className: 'placeholder:lowercase',
								}}
								errors={fields.company.errors}
							/>,
							<PublicStyledField
								key={4}
								className={'inline-block'}
								placeholder={`${t('common:form.fields.location.address')} 1`}
								inputProps={{
									...getInputProps(fields.address1, {
										type: 'text',
									}),
									autoComplete: 'address-line1',
									className: 'placeholder:lowercase',
								}}
								errors={fields.address1.errors}
							/>,
							<PublicStyledField
								key={5}
								className={'inline-block'}
								placeholder={`${t('common:form.fields.location.address')} 2`}
								inputProps={{
									...getInputProps(fields.address2, {
										type: 'text',
									}),
									autoComplete: 'address-line2',
									className: 'placeholder:lowercase',
								}}
								errors={fields.address2.errors}
							/>,
							<PublicStyledField
								key={6}
								className={'inline-block'}
								placeholder={t('common:form.fields.location.city')}
								inputProps={{
									...getInputProps(fields.city, {
										type: 'text',
									}),
									autoComplete: 'address-level2',
									className: 'placeholder:lowercase',
								}}
								errors={fields.city.errors}
							/>,
							fields.country.value === 'IT' || fields.country.value === 'US' ? (
								<PublicStyledSelect
									key={7}
									className={'inline-block'}
									placeholder={`${t('common:form.fields.location.province')}`.toLowerCase()}
									meta={fields.province}
									items={Provinces.getSelectOptions(
										fields.country.value === 'IT' ? 'italy' : 'usa',
									)}
									fieldErrors={fields.province.errors}
								/>
							) : (
								<span className="hidden">&nbsp;</span>
							),
							<PublicStyledField
								key={8}
								className={'inline-block'}
								placeholder={t('common:form.fields.location.zip')}
								inputProps={{
									...getInputProps(fields.zip, {
										type: 'text',
									}),
									autoComplete: 'postal-code',
									className: 'placeholder:lowercase',
								}}
								errors={fields.zip.errors}
							/>,
							<PublicStyledComboboxField
								key={9}
								className={'inline-block'}
								placeholder={`${t('common:form.fields.location.country')}`.toLowerCase()}
								meta={fields.country}
								items={Country.getSelectOptions(locale)}
								fieldErrors={fields.country.errors}
								searchPlaceholder={t('common:search.placeholder')}
								emptyText={t('common:search.noResults')}
							/>,
							<PublicStyledField
								key={10}
								className={'inline-block'}
								placeholder={t('common:form.fields.email')}
								inputProps={{
									...getInputProps(fields.email, {
										type: 'email',
									}),
									autoComplete: 'email',
									className: 'placeholder:lowercase',
								}}
								errors={fields.email.errors}
							/>,
							<PublicStyledField
								key={11}
								className={'inline-block'}
								placeholder={t('common:form.fields.phone')}
								inputProps={{
									...getInputProps(fields.phone, {
										type: 'tel',
									}),
									autoComplete: 'tel',
									className: 'placeholder:lowercase',
								}}
								errors={fields.phone.errors}
							/>,
							<PublicStyledSelect
								key={121}
								className={'inline-block'}
								placeholder={`${t('common:form.fields.reasonForContact')}`.toLowerCase()}
								meta={fields.reasonForContact}
								items={ReasonForContact.getSelectOptions(locale)}
								fieldErrors={fields.reasonForContact.errors}
							/>,
							<PublicStyledTextareaField
								key={13}
								textareaProps={{
									...getInputProps(fields.message, {
										type: 'text',
									}),
									placeholder:
										`${t('common:form.fields.consultation')}`.toLowerCase(),
									rows: 5,
								}}
								className="inline-block w-[60%]"
								errors={fields.message.errors}
							/>,
						]}
					/>
				</Text>

				<CheckboxField
					labelProps={{
						children: (
							<Trans
								t={t}
								i18nKey="common:form.disclaimer.message"
								components={[
									<LocalizedLink
										key="privacy-policy-link"
										to={routes.public.legal.privacy.route({})}
										className="underline"
									/>,
								]}
							/>
						),
					}}
					buttonProps={{
						...getInputProps(fields.acceptsTerms, {
							type: 'checkbox',
						}),
					}}
					className="col-span-full"
					errors={fields.acceptsTerms.errors}
				/>

				<CheckboxField
					labelProps={{
						children: t('common:form.newsletter.subscribe'),
					}}
					buttonProps={{
						...getInputProps(fields.newsletter, {
							type: 'checkbox',
						}),
					}}
					className="col-span-full"
					errors={fields.newsletter.errors}
				/>

				<Button
					disabled={isSubmitting}
					className={cn(
						'group rounded-full border border-brand-dusk bg-transparent text-brand-dusk hover:bg-brand-dusk/10',
						'z-10 flex items-center justify-center gap-sm',
						'transition-colors duration-150 ease-in-out',
						'absolute bottom-[10%] left-2/3 aspect-square h-[200px] w-[200px] -translate-x-1/2 select-none sm:h-[300px] sm:w-[300px] md:left-auto md:right-[12%] md:translate-x-0 lg:bottom-[2%] lg:h-[350px] lg:w-[350px]',
						// "xl:w-[450px]",
						isSubmitting && 'cursor-not-allowed opacity-50',
					)}
				>
					<div className="flex flex-col items-start justify-start gap-sm text-sm font-bold uppercase tracking-[0.2em]">
						<span>
							{isSubmitting
								? t('contact:form.sending')
								: t('contact:form.send')}
						</span>
						<Icon
							name="arrow-right"
							className="mr-auto h-11 w-11 transition-all duration-200 ease-in-out group-hover:ml-md"
						/>
					</div>
				</Button>
			</Form>

			<MarketingSpacer side={'top'} />

			<section className={'container flex items-end justify-between gap-xl'}>
				<div>
					<MarketingImage
						className={
							'size-[30vw] max-h-[200px] max-w-[200px] xl:max-h-[300px] xl:max-w-[300px]'
						}
						src={'/img/contact/SS Luxury Paper.webp'}
					/>
				</div>

				<div
					className={'flex flex-col items-start gap-x-xl gap-y-md md:flex-row'}
				>
					<div className={'flex flex-col gap-sm'}>
						<Text
							as={'h2'}
							weight={'light'}
							type={'serif'}
							size={'h4'}
							className={'uppercase'}
						>
							<SplitText as={'span'}>{t('contact:title')}</SplitText>
						</Text>
						<Text as={'p'} weight={'extralight'} size={'p'}>
							<SplitText element={'words'} as={'span'}>
								<span>info@sapienstone.com</span>
							</SplitText>
							<SplitText element={'words'} as={'span'}>
								<span>+39 0536 816883</span>
							</SplitText>
						</Text>
					</div>

					<div className={'flex flex-col gap-sm'}>
						<Text
							as={'h2'}
							weight={'light'}
							type={'serif'}
							size={'h4'}
							className={'uppercase'}
						>
							<SplitText as={'span'}>Showroom</SplitText>
						</Text>
						<a
							href={'https://maps.app.goo.gl/v76aqA2rcVM4Ci1v5'}
							target={'_blank'}
							rel={'noreferrer noopener'}
						>
							<Text as={'span'} weight={'extralight'} size={'p'}>
								<SplitText element={'words'} as={'span'}>
									<span>Sede central de Castellarano</span>
								</SplitText>
								<SplitText element={'words'} as={'span'}>
									<span>Via Guido Reni, 2 42014</span>
								</SplitText>
								<SplitText element={'words'} as={'span'}>
									<span>Castellarano RE- Italy</span>
								</SplitText>
							</Text>
						</a>
					</div>
				</div>
			</section>

			<LandingSection borderToBorderOnX containerClassName={'justify-center'}>
				<div className={'container w-fit'}>
					<Text
						as={'h2'}
						size={'pageTitle'}
						type={'serif'}
						weight={'light'}
						className={'text-center lowercase'}
					>
						<Trans
							t={t}
							i18nKey={'contact:newsletter'}
							components={[
								<span
									key={0}
									className={'inline-flex items-center justify-center gap-sm'}
								>
									(
									<MarketingImage
										className={
											'aspect-videos inline-flex max-h-[60px] w-[calc(clamp(50px,calc(4vw+2rem),7rem)*2)] items-end justify-center md:max-h-[80px] lg:max-h-[100px] 2xl:max-h-[120px]'
										}
										imageClassName={'object-bottom'}
										alt={'Fiori Viola'}
										src={'/img/contact/Fior-di-viola_1.webp'}
									/>
									)
								</span>,
								<span
									key={1}
									className={'inline-flex items-center justify-center gap-sm'}
								>
									(
									<MarketingImage
										className={
											'aspect-videos inline-flex max-h-[60px] w-[calc(clamp(50px,calc(4vw+2rem),7rem)*2)] items-end justify-center md:max-h-[80px] lg:max-h-[100px] 2xl:max-h-[120px]'
										}
										imageClassName={'object-bottom'}
										alt={'Fiori Viola'}
										src={'/img/contact/Ceppo-varese.webp'}
									/>
									)
								</span>,
							]}
						/>
					</Text>
					<NewsletterForm id={'newsletter-form-contact'} />
				</div>
			</LandingSection>

			{loaderData.stores && (
				<LandingSection id={'showrooms'}>
					<StoreMapLocationChooser
						stores={loaderData.stores}
						editable={false}
					/>
				</LandingSection>
			)}

			<LandingSection>
				<div className={'grid items-end gap-y-sm lg:grid-cols-2'} id={'faq'}>
					<Text
						as={'h2'}
						size={'h4'}
						weight={'light'}
						type={'serif'}
						className={'uppercase'}
					>
						{`${t('faq.title')}`.split(' ').map((item) => (
							<SplitText as={'span'} key={item}>
								{item}
							</SplitText>
						))}
						<span className={'font-bold'}>
							<SplitText as={'span'}>FAQ</SplitText>
						</span>
					</Text>

					<MarketingImage src={'/img/contact/SS Paper Logo Mockup.webp'} />
				</div>

				<Accordion
					type={'single'}
					className={'mt-md md:mt-lg xl:mt-xl'}
					collapsible
				>
					{new Array(8).fill(null).map((_, i) => (
						<AccordionItem
							value={String(i + 1)}
							key={i}
							className={'border-none'}
						>
							<AccordionTrigger>
								<Text as={'h3'} size={'h5'} weight={'light'} type={'serif'}>
									<Trans t={t} i18nKey={`faq.${i}.title`} />
								</Text>
							</AccordionTrigger>
							<AccordionContent className={'my-sm border-none'}>
								<Text
									as={'div'}
									size={'p'}
									weight={'light'}
									className={'!leading-[clamp(20px,calc(1.25vw+0.75rem),2rem)]'}
								>
									<Trans t={t} i18nKey={`faq.${i}.content`} />
								</Text>
							</AccordionContent>
						</AccordionItem>
					))}
				</Accordion>
			</LandingSection>
		</>
	)
}
